/* eslint-disable complexity */
import { getForwardedRef } from '@adac-mp/shared';
import React, {
	ChangeEvent,
	HTMLInputTypeAttribute,
	FocusEvent,
	MouseEvent,
	forwardRef,
	CSSProperties,
	useState,
	useEffect,
} from 'react';
import { CheckSuccess, HelperTextWithIcon, LoaderIcon } from '../../index';

interface IInputProps {
	id?: string;
	placeholder?: string;
	basic?: boolean;
	mandatory?: boolean;
	readonly?: boolean;
	value?: string;
	disabled?: boolean;
	size?: InputSize;
	className?: string;
	iconWrapperClassName?: string;
	maxLength?: number;
	type?: HTMLInputTypeAttribute;
	style?: React.CSSProperties;
	hasError?: boolean;
	hasWarning?: boolean;
	hasSucceeded?: boolean;
	helperText?: React.ReactNode;
	widthClasses?: string;
	icon?: React.ReactNode;
	autocomplete?: string;
	name?: string;
	iconRight?: React.ReactNode;
	loading?: boolean;
	wrapperStyle?: CSSProperties;
	helperTextStyle?: CSSProperties;
	withAutoFocus?: boolean;
	checked?: boolean;
	pattern?: string;
	ariaLabel?: string;
	inputMode?: 'text' | 'search' | 'none' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | undefined;
	onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
	onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
	onClick?: (event: MouseEvent<HTMLInputElement>) => void;
	onPaste?: (event) => void;
	onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
	'data-cy'?: string;
}

enum EInputSize {
	lg = 'text-xl px-4 py-3',
	md = 'text-lg px-4 py-[13.5px] md:text-xl md:py-3',
	mdEven = 'text-lg px-4 py-[13.5px]',
	sm = 'text-base px-4 py-[9px]',
	xs = 'text-base py-1 px-1.5',
}

enum EInputSizeActive {
	lg = 'pt-4 pb-1.5',
	md = 'pt-3.5 pb-[7px] md:pt-4 md:pb-1.5',
	mdEven = 'pt-3.5 pb-[7px]',
	sm = 'pt-[14px] pb-1',
	xs = 'text-base py-1 px-1.5',
}

enum EInputSimpleSize {
	lg = 'text-xl px-4 py-3',
	md = 'text-lg px-4 py-2 md:text-xl md:py-3',
	mdEven = 'text-lg px-4 py-2',
	sm = 'text-sm px-4 py-1.5',
	xs = 'text-base py-1 px-1',
}

enum EInputLabelSize {
	lg = 'text-xl top-3',
	md = 'text-lg top-[14px] md:top-3 md:text-xl',
	mdEven = 'text-lg top-[14px]',
	sm = 'text-sm top-1.5',
}

type InputSize = keyof typeof EInputSize;

const renderLabel = (size, icon, placeholder, active, mandatory, name?) => (
	<label
		htmlFor={name}
		className={`${mandatory && `after:content-['_*'] after:text-error`} ${
			EInputLabelSize[size]
		} absolute pointer-events-none duration-300 font-bold text-gray-300 transform translate-x-4 ${
			active ? 'text-xs -translate-y-[7px] md:-translate-y-[5px] md:text-xs' : ''
		}  peer-disabled:text-gray-500 overflow-hidden whitespace-nowrap overflow-ellipsis max-w-full pr-3 ${
			icon ? 'pl-[36px]' : ''
		}`}>
		{placeholder}
	</label>
);

type Ref = HTMLElement[] | HTMLElement;

// eslint-disable-next-line prefer-arrow-callback
export const Input = forwardRef<Ref, IInputProps>((props, ref) => {
	const {
		id,
		placeholder,
		basic,
		mandatory = false,
		value = '',
		disabled,
		readonly = false,
		size = 'md',
		className = '',
		iconWrapperClassName = '',
		maxLength,
		type,
		style = {},
		hasError = false,
		hasWarning = false,
		hasSucceeded = false,
		helperText = '',
		widthClasses,
		icon,
		iconRight,
		autocomplete,
		loading,
		name,
		wrapperStyle,
		helperTextStyle,
		withAutoFocus = false,
		checked = false,
		pattern = '',
		ariaLabel = '',
		inputMode = 'text',
		onChange = () => {},
		onBlur = () => {},
		onFocus,
		onClick,
		onPaste,
		onKeyDown,
		'data-cy': dataCy = undefined,
	} = props;
	const [active, setActive] = useState(Boolean(value));
	const errorClass = hasError && 'shadow-innerError';
	const warningClass = hasWarning && 'shadow-innerWarning';
	const successClass = hasSucceeded && 'shadow-innerSuccess';
	useEffect(() => {
		setActive(Boolean(value));
	}, [value]);
	return placeholder && !basic ? (
		<>
			<div className={`${widthClasses}`}>
				<div className="flex flex-col relative overflow-hidden mb-1.5" style={wrapperStyle}>
					<input
						ref={(el) => getForwardedRef(el, ref)}
						id={id}
						autoFocus={withAutoFocus}
						data-cy={dataCy ?? `input: ${name}`}
						readOnly={readonly}
						disabled={disabled}
						pattern={pattern}
						aria-label={ariaLabel}
						inputMode={inputMode}
						style={style}
						className={`${EInputSize[size]} ${
							active ? EInputSizeActive[size] : ''
						} peer appearance-none border-none flex items-center w-full font-bold bg-white rounded focus-visible:outline-none disabled:shadow-none disabled:text-gray-500 ${
							!hasError && !hasSucceeded && !hasWarning ? 'hover:shadow-innerHover' : ''
						}  active:shadow-innerWarning  focus:shadow-innerWarning
				${errorClass} ${warningClass} ${successClass} ${icon ? 'pl-[60px]' : ''}
				 ${className}`}
						value={value}
						placeholder={size === 'xs' ? placeholder : ' '}
						maxLength={maxLength}
						type={type}
						autoComplete={autocomplete}
						name={name}
						onChange={onChange}
						onBlur={onBlur}
						onFocus={onFocus}
						onClick={onClick}
						onPaste={onPaste}
						onKeyDown={onKeyDown}
					/>
					{size !== 'xs' && renderLabel(size, icon, placeholder, active, mandatory, name)}
					{icon && (
						<span
							className={`absolute inline-flex pointer-events-none top-1/2 -translate-y-1/2 left-4 ${iconWrapperClassName}`}>
							{icon}
						</span>
					)}
					{iconRight && (
						<span className={`absolute pointer-events-none right-0 top-0 ${EInputSimpleSize[size]} `}>
							{iconRight}
						</span>
					)}
					{loading && (
						<span className="absolute right-4 top-[calc(50%_-_12px)] animate-spin inline-flex items-center justify-center">
							<LoaderIcon />
						</span>
					)}
					{checked && (
						<span className="absolute right-4 top-[calc(50%_-_10px)] inline-flex">
							<CheckSuccess />
						</span>
					)}
				</div>
				<HelperTextWithIcon
					hasError={hasError}
					hasSucceeded={hasSucceeded}
					hasWarning={hasWarning}
					helperText={helperText}
					helperTextStyle={helperTextStyle}
				/>
			</div>
		</>
	) : (
		<>
			<input
				ref={(el) => el && ref && getForwardedRef(el, ref)}
				id={id}
				autoFocus={withAutoFocus}
				data-cy={dataCy ?? `input: ${name}`}
				readOnly={readonly}
				style={style}
				className={`${
					EInputSimpleSize[size]
				} flex items-center w-full font-bold bg-white border border-solid border-gray-600 rounded focus-visible:outline-none ${
					disabled ? 'placeholder-gray-400 cursor-default caret-transparent' : 'placeholder-gray-300'
				} ${errorClass} ${warningClass} ${successClass} ${className}`}
				value={value}
				placeholder={placeholder}
				maxLength={maxLength}
				type={type}
				autoComplete={autocomplete}
				name={name}
				onBlur={onBlur}
				onFocus={onFocus}
				onChange={onChange}
				onClick={onClick}
				onPaste={onPaste}
				onKeyDown={onKeyDown}
			/>
			<HelperTextWithIcon
				hasError={hasError}
				hasSucceeded={hasSucceeded}
				hasWarning={hasWarning}
				helperText={helperText}
				helperTextStyle={{ marginTop: '10px', ...helperTextStyle }}
			/>
		</>
	);
});

Input.displayName = 'Input';
